/**
 * Settings page.
 */
import React, { useState, useEffect } from "react";
import { Box, Grid, Tab, Tabs, Divider, IconButton } from "@mui/material";
import { Icon } from "@iconify/react";
import MuiDrawer from "@mui/material/Drawer";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import SettingsAccount from "./settings.account";
import SettingsPreferences from "./settings.preferences";
import SettingsDocuments from "./settings.documents";
import { VButton, VText } from "form";
import { useAsterController } from "context";

/**
 * Tabbed settings for account, preferences, and documents.
 */
const Settings = () => {
  const { t } = useTranslation("common");

  const [controller] = useAsterController();
  const { mobile } = controller;
  const location = useLocation();

  const [tab, setTab] = useState(0);
  const [wrap, setWrap] = useState(true);

  useEffect(() => mobile && setTab(0), [mobile]);
  useEffect(() => {
    if (location.pathname.includes("document")) setTab(2);
  }, [location.pathname]);

  const handleSetTabValue = (_, newValue) => setTab(newValue);

  return (
    <>
      <MuiDrawer anchor="left" open={!wrap} onClose={() => setWrap(!wrap)}>
        <Box className="v-header w-60">
          <VButton variant="text" color="secondary" className={`text-sm normal-case w-full my-1 text-black justify-start py-3 pl-5 ${tab === 0 ? "v-bg-side-active" : ""}`} onClick={() => setTab(0)}>
            {t("Account")}
          </VButton>
          <VButton variant="text" color="secondary" className={`text-sm normal-case w-full my-1 text-black justify-start py-3 pl-5 ${tab === 1 ? "v-bg-side-active" : ""}`} onClick={() => setTab(1)}>
            {t("Preferences")}
          </VButton>
          <VButton variant="text" color="secondary" className={`text-sm normal-case w-full my-1 text-black justify-start py-3 pl-5 ${tab === 2 ? "v-bg-side-active" : ""}`} onClick={() => setTab(2)}>
            {t("Documents")}
          </VButton>
        </Box>
      </MuiDrawer>

      <Box className="m-0 md:m-6 v-bg-body rounded-none md:rounded-md flex-1">
        <Box className="mx-auto v-bg-body">
          <Box className="h-15 md:flex hidden px-14 items-center pt-3">
            <Tabs orientation="horizontal" value={tab} onChange={handleSetTabValue} className="h-12 items-end">
              <Tab label={t("Account")} className="min-h-0 py-2 flex-row items-center gap-2 h-12 normal-case" />
              <Tab label={t("Preferences")} className="min-h-0 py-2 flex-row items-center gap-2 h-12 normal-case" />
              <Tab label={t("Documents")} className="min-h-0 py-2 flex-row items-center gap-2 h-12 normal-case" />
            </Tabs>
          </Box>

          <Divider className="bg-gray-200 dark:bg-gray-500 hidden md:flex" />

          <Grid container spacing={2} className="px-4">
            <Grid item xs={12} className="flex md:hidden justify-between items-center">
              <VText className="border-solid border-0 border-b-2 border-primary font-bold flex items-center gap-1 py-1">
                {tab === 0 && (
                  <>
                    <Icon className="mb-0" icon="ic:sharp-menu-book" />
                    {t("Account")}
                  </>
                )}
                {tab === 1 && (
                  <>
                    <Icon className="mb-0" icon="ic:outline-comment" />
                    {t("Preferences")}
                  </>
                )}
                {tab === 2 && (
                  <>
                    <Icon className="mb-0" icon="el:download" />
                    {t("Document")}
                  </>
                )}
              </VText>
              <IconButton component="label" onClick={() => setWrap(false)}>
                <Icon icon="heroicons-solid:menu-alt-1" />
              </IconButton>
            </Grid>
          </Grid>

          <Box className="px-4 md:px-14 md:py-8 mt-4">
            {tab === 0 && <SettingsAccount />}
            {tab === 1 && <SettingsPreferences />}
            {tab === 2 && <SettingsDocuments />}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default Settings;
